<template lang="pug">
.cart-page
  Breadcrumbs(:list="breadcrumbs")

  h1.cart-page__title(v-t="'cart'")

  .cart-page__body
    .cart-page__container
      span.cart-page__text(v-if="!cartIsLoaded" v-t="'loading'")
      span.cart-page__text(v-else-if="cartIsEmpty" v-t="'cartIsEmpty'")
      .cart-page__main(v-else)
        .cart-page__main-header
          .cart-page__main-header-checkbox
            CheckboxInput.cart-page__checkbox(v-model="chooseAll")
            span.cart-page__checkbox-label {{ $t("chooseAll") }}
          span.cart-page__main-header-action(v-if="chooseAll" @click="deleteSelected") {{ $t("deleteSelected") }}
        .cart-page__goods
          .cart-page__product(v-for="product in cartItems" :key="product.id" :class="{'cart-page__product--out-of-stocks': !stocksOk(product)}")
            .cart-page__product-card
              CheckboxInput(v-model="chooseAll")
              img.cart-page__product-cover(v-if="cover(product)" :src="cover(product)" :alt="product.title")
              .cart-page__product-cover(v-else)
              .cart-page__product-body
                .cart-page__product-body-brand {{ product.brand }}
                .cart-page__product-body-title {{ product.title }}
                Currency.cart-page__product-body-right(type="money" is-reversed size="16" real)
                  span.cart-page__product-body-price {{ (product.max_price / 100).toFixed(2).replace('.00', '') }}
            .cart-page__product-bottom
              .cart-page__product-bottom-delete(@click="deleteById(product.id)")
                img.cart-page__product-bottom-delete-icon(:src="require('~/assets/img/icons/marketplace-delete.svg')" alt="delete")
                span.cart-page__product-bottom-delete-text(v-t="'delete'")
              .cart-page__product-bottom-counter
                .cart-page__product-bottom-counter-action(@click="dropFromCart(product)") -
                .cart-page__product-bottom-counter-amount {{ amountInCart(product) }}
                .cart-page__product-bottom-counter-action(@click="addToCart(product)")
                  template(v-if="canAdd(product)")
                    | +

    .cart-page__container(v-if="!cartIsEmpty")
      .cart-page__container-title {{ $t("yourCart") }}
      .cart-page__details
        .cart-page__details-head
          .cart-page__details-head-label {{ $t("goods") }} ({{ count }})
          Currency(type="money" is-reversed size="16" real)
            .cart-page__details-head-amount {{ totalWithoutDiscount }}
        .cart-page__details-subtitle {{ $t("yourPriceWithDiscount") }}

        .cart-page__slider-container
          input.cart-page__slider(type="range" :min="cartMinPrice" :max="cartMaxPrice" v-model="cartPriceInSlider" step="0.1")

        .cart-page__your-price-block
          p.cart-page__your-price {{ $t("yourPrice") }}:
          .cart-page__your-price-amount
            Currency(type="money" is-reversed size="16" real) {{ total }}
            span.cart-page__your-price-amount-separator +
            .cart-page__your-price-amount-coins
              Currency.cart-page__your-price-amount-coins-amount(type="coins" is-reversed size="20") {{ coins }}
              //.cart-page__your-price-amount-coins-max MAX
        p.cart-page__excluding-vat(v-t="'priceWithoutDeliveryText'")

      .cart-page__summary
        .cart-page__summary-row
          .cart-page__summary-row-label {{ $t("totalPay") }}:
          Currency(type="money" is-reversed size="16" real)
            .cart-page__summary-row-value {{ total }}
        Button.cart-page__summary-button(v-if="true" is-primary @click="startCheckout")
          | {{ $t("buttonPay") }}
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs'
import { mapGetters, mapState } from 'vuex'
import CheckboxInput from '~/components/CheckboxInput/CheckboxInput'
import StoreService from '~/services/StoreService'

export default {
  components: {
    Breadcrumbs,
    CheckboxInput
  },

  data () {
    return {
      cartIsLoaded: false,
      cart: undefined,
      chooseAll: false,
      cartPriceInSlider: undefined
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['isAdmin']),

    breadcrumbs () {
      return [
        { title: this.$t('store'), to: '/store/marketplace' },
        { title: this.$t('cart') }
      ]
    },

    cartIsEmpty () {
      return !this.cart?.content?.items?.length
    },

    cartItems () {
      return this.cart?.content?.items?.filter((item, index, array) => array.findIndex(n => n.id === item.id) === index).sort((a, b) => b.stocks - a.stocks)
    },

    cartMinPrice () {
      return this.cart?.content?.items?.filter(item => item.stocks > 4)?.map(item => item.min_price).reduce((a, b) => a + b, 0)
    },

    cartMaxPrice () {
      return this.cart?.content?.items?.filter(item => item.stocks > 4)?.map(item => item.max_price).reduce((a, b) => a + b, 0)
    },

    coinsForSlider () {
      if (this.cartPriceInSlider !== undefined) {
        return (this.cartMaxPrice - this.cartPriceInSlider) * 100
      }
      return undefined
    },

    count () {
      return this.cart?.content?.items?.filter(item => item.stocks > 4)?.length
    },

    coins () {
      return (this.coinsForSlider !== undefined ? this.coinsForSlider : this.cart?.content?.coins?.map(d => d.amount).reduce((b, a) => b + a, 0)).toFixed(0)
    },

    totalWithoutDiscount () {
      return this.cart?.content?.items?.filter(item => item.stocks > 4)?.map(item => item.max_price / 100).reduce((b, a) => b + a, 0).toFixed(2).replace('.00', '')
    },

    total () {
      return ((this.cartPriceInSlider || this.cart?.content?.total) / 100).toFixed(2).replace('.00', '')
    }
  },

  mounted () {
    this.loadCart()
  },

  methods: {
    async loadCart () {
      this.cart = (await StoreService.getCart(this.$axios))?.data?.data?.cart
      console.log(this.cart)
      this.cartIsLoaded = true
      this.cartPriceInSlider = this.cart?.content?.total
    },

    async addToCart (item) {
      this.$nuxt.$loading.start()
      await StoreService.addToCart(this.$axios, item?.id, 0)
      window?.fbq?.('track', 'AddToCart')
      this.$gtag.event('add_to_cart', { account_id: this.user?.id, product: item.id })
      await this.loadCart()
      this.$nuxt.$loading.finish()
    },

    async dropFromCart (item) {
      this.$nuxt.$loading.start()
      await StoreService.dropFromCart(this.$axios, item?.id)
      this.$gtag.event('drop_from_cart', { account_id: this.user?.id, product: item.id })
      await this.loadCart()
      this.$nuxt.$loading.finish()
    },

    deleteById (productId) {
      for (const item of this.cart?.content?.items.filter(item => item.id === productId)) {
        this.dropFromCart(item)
      }
    },

    deleteSelected () {
      for (const item of this.cart?.content?.items) {
        this.dropFromCart(item)
      }
    },

    amountInCart (product) {
      return this.cart?.content?.items?.map(item => item.id).filter(item => item === product.id)?.length
    },

    stocksOk (product) {
      return product.stocks > 4
    },

    canAdd (product) {
      return this.amountInCart(product) < product.stocks && this.amountInCart(product) < 4
    },

    cover (product) {
      return product?.images?.[0]
    },

    startCheckout () {
      this.$router.push(this.localeLocation('/store/checkout'))
    }
  }
}
</script>

<style lang="scss" scoped>
.cart-page {
  padding: 16px;

  @media (min-width: $breakpointDesktopWide) {
    padding: 44px 115px;
  }

  &__title {
    margin-bottom: 16px;
    color: $white;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;

    @media (min-width: $breakpointDesktopWide) {
      margin-top: 44px;
      margin-bottom: 27px;
      font-size: 32px;
      line-height: 36px;
    }
  }

  &__text {
    display: block;
    color: $white;
    font-size: 20px;
    font-weight: 500;
    line-height: 24px;
    padding: 16px;

    @media (min-width: $breakpointDesktopWide) {
      padding: 24px;
      font-size: 24px;
      line-height: 32px;
    }
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 24px;

    @media (min-width: $breakpointTablet) {
      grid-template-columns: 1fr 1fr;
      grid-gap: 20px;
    }

    @media (min-width: $breakpointDesktopWide) {
      grid-template-columns: 3fr 2fr;
      grid-gap: 24px;
    }
  }

  &__main-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;

    @media (min-width: $breakpointDesktopWide) {
      padding: 24px;
    }

    &-checkbox {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &-action {
      color: $red70;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }

  &__checkbox {
    &-label {
      margin-left: 8px;
      color: $white;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }

  &__container {
    background: $blue-grey70;
    border-radius: 16px;
    overflow: hidden;
    height: max-content;

    &-title {
      padding: 16px;
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
      color: $white;
      border-bottom: 1px solid $blue-grey40;

      @media (min-width: $breakpointDesktopWide) {
        padding: 24px;
      }
    }
  }

  &__goods {
    display: grid;
    grid-template-columns: 1fr;
  }

  &__product {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 2px;

    &--out-of-stocks {
      opacity: 0.4;
    }

    &-card {
      display: grid;
      grid-template-columns: max-content 78px 1fr;
      grid-gap: 16px;
      align-items: flex-start;
      padding: 16px;
      cursor: pointer;
      border-top: 1px solid $blue-grey40;

      @media (min-width: $breakpointDesktopWide) {
        padding: 24px;
      }
    }

    &-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 16px 20px;

      &-delete {
        display: grid;
        grid-template-columns: 12px max-content;
        grid-gap: 8px;
        align-items: center;
        cursor: pointer;

        &-icon {
          width: 12px;
          height: 12px;
        }

        &-text {
          color: $white;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
        }
      }

      &-counter {
        display: grid;
        grid-template-columns: max-content max-content max-content;
        grid-gap: 14px;
        color: $white;
        font-size: 16px;
        font-weight: 500;
        line-height: 20px;

        &-amount {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 8px;
          min-width: 24px;
          height: 24px;
          background: $blue-grey50;
          border-radius: 4px;
        }

        &-action {
          cursor: pointer;
        }
      }
    }

    &-cover {
      height: 78px;
      width: 78px;
      border-radius: 16px;
      background-color: $white;
    }

    &-body {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;
      padding: 0 4px;

      &-title {
        font-size: 16px;
        line-height: 20px;
        font-weight: 700;
        color: $white;
        padding-right: 16px;
      }

      &-brand {
        font-size: 10px;
        font-weight: 500;
        line-height: 14px;
        letter-spacing: 0.2px;
        text-transform: uppercase;
        color: $blue-grey10;
      }

      &-right {
        justify-content: flex-start;
      }

      &-price {
        display: block;
        font-size: 14px;
        line-height: 20px;
        font-weight: 700;
        color: $white;
      }
    }
  }

  &__details {
    padding: 16px;

    @media (min-width: $breakpointDesktopWide) {
      padding: 24px;
    }

    &-head {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-label {
        color: $white;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
      }

      &-amount {
        color: $white;
        font-size: 16px;
        font-weight: 700;
        line-height: 24px;
      }
    }

    &-subtitle {
      margin: 20px 0 16px;
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
    }
  }

  &__your-price {
    margin-right: 8px;
    color: $blue-grey10;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;

    &-block {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &-amount {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      height: 32px;
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;

      &-separator {
        padding: 0 8px;
        color: $blue-grey10;
        font-size: 20px;
        font-weight: 500;
        line-height: 28px;
      }

      &-coins {
        display: grid;
        align-items: center;
        grid-template-columns: max-content max-content;

        &-amount {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 4px 12px 4px 8px;
          background: $blue-grey50;
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        &-max {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 4px 8px;
          color: $primary60;
          background: rgba($primary60, 0.2);
          font-size: 18px;
          font-style: normal;
          font-weight: 500;
          line-height: 22px;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
    }
  }

  &__excluding-vat {
    margin-top: 16px;
    color: $blue-grey10;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  &__summary {
    padding: 24px 16px;
    border-top: 1px solid $blue-grey40;

    &-row {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-label {
        color: $white;
        font-size: 18px;
        font-weight: 700;
        line-height: 24px;
      }

      &-value {
        color: $white;
        font-size: 18px;
        font-weight: 700;
        line-height: 24px;
      }
    }

    &-button {
      width: 100%;
      margin-top: 20px;
    }
  }

  &__slider {
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    background: $blue-grey50;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 32px;
      height: 32px;
      background: $primary60;
      border-radius: 50%;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 32px;
      height: 32px;
      background: $primary60;
      border-radius: 50%;
      cursor: pointer;
    }

    &-container {
      width: 100%;
      margin: 24px 0;
    }
  }
}
</style>
